/**
 * ラリー詳細のシート中身
 *
 * 地図と Header は一覧と共有のシェルが持つので、ここはシートのヘッダーと中身だけを組み立てる。
 * URL が /rallies/:rally_id のあいだだけ動き、一覧に戻ると何も返さない。
 */

import { ChevronDown } from 'lucide-react'
import { type ReactNode, type RefObject, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import {
  createStampsBulk,
  dismissVisitSuggestion,
  getVisitSuggestions,
  restoreVisitSuggestion,
} from '@/api/services/stamp-rally'
import type { CreateItemParams, StampRallyItem, VisitSuggestion } from '@/api/types/stamp-rally'
import type { SortOption } from '@/components/shared/map/rally-filter-bar'
import { matchesStampFilter } from '@/components/shared/map/stamp-filter'
import type { RallyPlacesResult } from '@/components/shared/map/use-rally-places'
import { SheetNavHeader } from '@/components/shared/sheet-nav-header'
import type { BottomSheetRef } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import type { Coords } from '@/hooks/useCurrentPosition'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { confirmDialog } from '@/lib/confirm-dialog'
import { PATHS } from '@/routes/paths'
import { useStampRallyStore } from '@/store/useStampRallyStore'

import { RallyFormPanel } from '../components/rally-form-panel'
import { ItemFormPanel } from './components/item-form-panel'
import { RallyItemFilterBar } from './components/rally-item-filter-bar'
import { type ItemSort, RallyListView } from './components/rally-list-view'
import { VisitSuggestionBanner } from './components/visit-suggestion-banner'
import { filterGroups, prefecturesOf } from './lib'

/** シートの中身。地図を隠さないよう、編集も別シートにせずここへ差し替える */
type DetailMode = 'list' | 'item' | 'rally'

const SORT_LABELS: Record<ItemSort, string> = {
  official: '公式の順番',
  distance: '現在地から近い順',
}

const SORT_OPTIONS: SortOption[] = (Object.keys(SORT_LABELS) as ItemSort[]).map((value) => ({
  value,
  label: SORT_LABELS[value],
}))

const isItemSort = (value: string | number): value is ItemSort => value in SORT_LABELS

type Args = {
  /** null なら一覧を見ている。詳細の取得も止める */
  rallyId: number | null
  rallyPlaces: RallyPlacesResult
  origin: Coords | null
  requestPosition: () => Promise<Coords | null>
  /** 地図にも同じ絞り込みを掛けるため、状態はシェルが持つ */
  keyword: string
  onKeywordChange: (value: string) => void
  selectedPrefectures: string[]
  /** リストから開いたスポットの吹き出しを地図にも出す */
  onActivatePlace: (placeId: number | null) => void
  sheetRef: RefObject<BottomSheetRef | null>
  snap: { peek: number; half: number; full: number }
}

export type RallyDetailPanel = {
  title: string | null
  header: ReactNode
  body: ReactNode
  /** 地図の絞り込みボタンに並べる選択肢 */
  prefectures: string[]
  sortOptions: SortOption[]
  sort: string
  onSortChange: (value: string) => void
  /** 地図のポップアップからスポットを開く */
  selectItem: (item: StampRallyItem) => void
  /** 地図側でスタンプが変わったときにリストを追いつかせる */
  refresh: () => void
}

export const useRallyDetailPanel = ({
  rallyId,
  rallyPlaces,
  origin,
  requestPosition,
  keyword,
  onKeywordChange,
  selectedPrefectures,
  onActivatePlace,
  sheetRef,
  snap,
}: Args): RallyDetailPanel => {
  const navigate = useNavigate()
  const [mode, setMode] = useState<DetailMode>('list')
  const [sort, setSort] = useState<ItemSort>('official')
  // 実体ではなく ID で持つ。スタンプを押した結果がそのままパネルに映るようにするため
  const [editingItemId, setEditingItemId] = useState<number | null>(null)
  const [suggestions, setSuggestions] = useState<VisitSuggestion[]>([])
  const { isSubmitting: isApprovingSuggestions, run: runApproveSuggestions } = useSubmitLock()

  const detail = useStampRallyStore((s) => s.detail)
  const isDetailLoading = useStampRallyStore((s) => s.isDetailLoading)
  const fetchDetail = useStampRallyStore((s) => s.fetchDetail)
  const clearDetail = useStampRallyStore((s) => s.clearDetail)
  const addItem = useStampRallyStore((s) => s.addItem)
  const editItem = useStampRallyStore((s) => s.editItem)
  const removeItem = useStampRallyStore((s) => s.removeItem)
  const toggleStamp = useStampRallyStore((s) => s.toggleStamp)
  const updateRally = useStampRallyStore((s) => s.updateRally)
  const removeRally = useStampRallyStore((s) => s.removeRally)

  useEffect(() => {
    if (rallyId === null) {
      clearDetail()
      return
    }
    void fetchDetail(rallyId)
    return clearDetail
  }, [rallyId, fetchDetail, clearDetail])

  useEffect(() => {
    setMode('list')
    setEditingItemId(null)
  }, [rallyId])

  useEffect(() => {
    if (rallyId === null) {
      setSuggestions([])
      return
    }
    const controller = new AbortController()
    // 提案が取れなくても本体の表示は続けたいのでエラーは握りつぶす
    getVisitSuggestions(rallyId, controller.signal)
      .then(setSuggestions)
      .catch(() => undefined)
    return () => controller.abort()
  }, [rallyId])

  const handleChangeSort = async (next: ItemSort) => {
    if (next !== 'distance' || origin) {
      setSort(next)
      return
    }
    const position = await requestPosition()
    if (!position) {
      toast.error('現在地を取得できませんでした')
      return
    }
    setSort(next)
  }

  const backToList = () => {
    setMode('list')
    setEditingItemId(null)
  }

  /** null を渡すと追加。地図のピンからもリストからもここへ集約する */
  const openItem = (item: StampRallyItem | null) => {
    setEditingItemId(item?.id ?? null)
    setMode('item')
    onActivatePlace(item?.place_id ?? null)
    // 既存スポットはどのピンを見ているか分かるよう地図を残し、追加は入力欄を優先する
    sheetRef.current?.snapTo(item ? snap.half : snap.full)
  }

  const openRallyForm = () => {
    setMode('rally')
    sheetRef.current?.snapTo(snap.full)
  }

  const handleToggleStamp = async (item: StampRallyItem) => {
    if (rallyId === null) return
    await toggleStamp(item.id, !item.stamped_at)
    rallyPlaces.refetch()
  }

  const handleSubmitItem = async (params: CreateItemParams) => {
    if (rallyId === null) return
    // 編集で直せるのはラリー固有のメタだけ。名前と場所は ItemPlaceSection が受け持つ
    const ok = editingItemId
      ? await editItem(rallyId, editingItemId, { external_url: params.external_url, note: params.note }).then(
          () => true,
        )
      : await addItem(rallyId, params)
    if (!ok) return
    rallyPlaces.refetch()
    backToList()
  }

  const handleDeleteItem = async () => {
    if (rallyId === null || !editingItemId) return
    await removeItem(rallyId, editingItemId)
    rallyPlaces.refetch()
    backToList()
  }

  const handleDeleteRally = async () => {
    if (rallyId === null) return
    if (
      !(await confirmDialog({
        title: 'このラリーを削除しますか？',
        description: 'スポットとスタンプもすべて消えます。',
        destructive: true,
      }))
    )
      return
    await removeRally(rallyId)
    rallyPlaces.refetch()
    navigate(PATHS.RALLIES.LIST)
  }

  const handleApproveSuggestions = async (itemIds: number[]) => {
    if (rallyId === null) return
    await runApproveSuggestions(async () => {
      try {
        const result = await createStampsBulk(rallyId, itemIds)
        toast.success(`${result.created} 件をチェックしました`)
        setSuggestions([])
        await fetchDetail(rallyId)
        rallyPlaces.refetch()
      } catch {
        toast.error('チェックに失敗しました')
      }
    })
  }

  const handleDismissSuggestion = async (suggestion: VisitSuggestion) => {
    if (rallyId === null) return
    try {
      await dismissVisitSuggestion(rallyId, suggestion.item_id, suggestion.visit_id)
    } catch {
      toast.error('候補から外せませんでした')
      return
    }
    // 消したあと、同じ場所の別の訪問が繰り上がるので引き直す
    setSuggestions((prev) => prev.filter((s) => s.item_id !== suggestion.item_id))
    getVisitSuggestions(rallyId)
      .then(setSuggestions)
      .catch(() => undefined)
    toast.success(`「${suggestion.name}」を候補から外しました`, {
      action: {
        label: '元に戻す',
        onClick: () => {
          restoreVisitSuggestion(rallyId, suggestion.item_id, suggestion.visit_id)
            .then(() => getVisitSuggestions(rallyId))
            .then(setSuggestions)
            .catch(() => toast.error('戻せませんでした'))
        },
      },
    })
  }

  const color = detail?.color ?? '#F97316'
  const percent = Math.round((detail?.progress ?? 0) * 100)
  const allGroups = rallyId === null ? [] : (detail?.groups ?? [])
  const groups = filterGroups(allGroups, keyword, selectedPrefectures)
  const editingItem = allGroups.flatMap((group) => group.items).find((item) => item.id === editingItemId) ?? null
  const visibleItems = groups
    .flatMap((group) => group.items)
    .filter((item) => matchesStampFilter(item.stamped_at, rallyPlaces.stampFilter))

  const shared = {
    prefectures: prefecturesOf(allGroups),
    sortOptions: SORT_OPTIONS,
    sort,
    onSortChange: (value: string) => isItemSort(value) && void handleChangeSort(value),
    selectItem: openItem,
    refresh: () => rallyId !== null && void fetchDetail(rallyId),
  }

  if (rallyId === null) return { ...shared, title: null, header: null, body: null }

  const header = (() => {
    if (mode === 'item') {
      return <SheetNavHeader onBack={backToList} title={editingItem?.name ?? 'スポットを追加'} />
    }
    if (mode === 'rally') {
      return <SheetNavHeader onBack={backToList} title="ラリーを編集" />
    }
    return (
      <SheetNavHeader
        onBack={() => navigate(PATHS.RALLIES.LIST)}
        title={
          <span className="tabular-nums text-muted-foreground">
            {detail ? `${visibleItems.length} 件のスポット` : '読み込み中'}
          </span>
        }
        action={
          <div className="flex shrink-0 items-center gap-1.5">
            <Button size="sm" variant="outline" className="px-3!" onClick={openRallyForm}>
              編集
            </Button>
            {/* 追加は分割ボタン。単体追加を主、まとめて登録を右のプルダウンに置く */}
            <div className="flex h-8 items-center overflow-hidden rounded-md border border-input shadow-xs">
              <Button size="sm" variant="ghost" className="h-full rounded-none px-3!" onClick={() => openItem(null)}>
                追加
              </Button>
              <span className="h-5 w-px bg-border" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-full rounded-none px-1.5!" aria-label="追加メニュー">
                    <ChevronDown size={15} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="bottom" align="end" avoidCollisions={false} className="z-[10000000]">
                  <DropdownMenuItem
                    onClick={() => navigate(PATHS.RALLIES.IMPORT.replace(':rally_id', String(rallyId)))}
                  >
                    まとめて登録
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        }
      />
    )
  })()

  const body = (() => {
    if (mode === 'item') {
      return (
        <ItemFormPanel
          key={editingItemId ?? 'new'}
          item={editingItem}
          origin={origin}
          onSubmit={handleSubmitItem}
          onDelete={editingItem ? handleDeleteItem : undefined}
          onToggleStamp={(item) => void handleToggleStamp(item)}
          onPlaceChanged={() => {
            void fetchDetail(rallyId)
            rallyPlaces.refetch()
          }}
        />
      )
    }
    if (mode === 'rally') {
      if (!detail) return null
      return (
        <RallyFormPanel
          rally={detail}
          onSubmit={async (params) => {
            await updateRally(rallyId, params)
            backToList()
          }}
          onDelete={() => void handleDeleteRally()}
        />
      )
    }
    return (
      <div className="p-3">
        {isDetailLoading && !detail ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        ) : (
          <>
            {detail && detail.total_count > 0 && (
              <div className="mb-3">
                <div className="flex items-baseline justify-between text-sm text-muted-foreground">
                  <span className="tabular-nums">
                    {detail.stamped_count} / {detail.total_count}
                  </span>
                  <span className="tabular-nums">{percent}%</span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-primary/15">
                  <div className="h-full transition-all" style={{ width: `${percent}%`, backgroundColor: color }} />
                </div>
              </div>
            )}
            <VisitSuggestionBanner
              suggestions={suggestions}
              isSubmitting={isApprovingSuggestions}
              onApprove={(itemIds) => void handleApproveSuggestions(itemIds)}
              onDismiss={(suggestion) => void handleDismissSuggestion(suggestion)}
            />
            <RallyItemFilterBar keyword={keyword} onKeywordChange={onKeywordChange} />
            <RallyListView
              groups={groups}
              filter={rallyPlaces.stampFilter}
              sort={sort}
              origin={origin}
              color={color}
              onToggle={(item) => void handleToggleStamp(item)}
              onOpenItem={openItem}
            />
          </>
        )}
      </div>
    )
  })()

  return {
    ...shared,
    title: detail?.title ?? null,
    header,
    body,
  }
}
